import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import GeoLocationInput from "@/components/GeoLocationInput";
import FileUploadZone from "@/components/FileUploadZone";
import DichiarazioniBlock from "@/components/DichiarazioniBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { enrichLabelWithYear } from "@/lib/exerciseYear";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Briefcase, ChevronRight } from "lucide-react";
import { FI, YN } from "@/components/FormFields";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import ClaimsInfoSheet, { emptyClaim } from "@/components/ClaimsInfoSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ========== CIS CONFIG - PROFESSIONI ECONOMICO-GIURIDICHE (dal PDF Furness/Lloyd's) ========== */
const CIS_ECO_CONFIG = {
  professioni: [
    "Avvocato", "Commercialista", "Consulente del Lavoro",
    "Ragioniere Commercialista", "Studio Legale", "Studio di Commercialisti",
    "Studio Associato Legale e Contabile", "Altro (specificare)",
  ],
  categorieDanno: [
    { id: "attivita_standard", label: "Attività standard" },
    { id: "attivita_edp", label: "Attività EDP" },
    { id: "attivita_sindacale", label: "Attività sindacale, consigliere d'amministrazione" },
    { id: "altro", label: "Altro (specificare)" },
  ],
  incarichi: [],
};

/* ============ PROFESSIONI ============ */
const CATEGORIES = [
  { id: "avvocato", label: "Avvocato", template: "scudo" },
  { id: "commercialista", label: "Commercialista", template: "scudo" },
  { id: "consulente_lavoro", label: "Consulente del Lavoro", template: "scudo" },
  { id: "ragioniere", label: "Ragioniere Commercialista", template: "scudo" },
  { id: "studio_legale", label: "Studio Legale", template: "scudo" },
  { id: "studio_commercialisti", label: "Studio di Commercialisti", template: "scudo" },
  { id: "studio_consulenti", label: "Studio di Consulenti del Lavoro", template: "scudo" },
  { id: "studio_comm_cons", label: "Studio di Commercialisti e Consulenti del Lavoro", template: "scudo" },
  { id: "studio_associato", label: "Studio Associato Legale e Contabile", template: "scudo" },
  { id: "centro_edp", label: "Centro Operatori EDP", template: "caf" },
  { id: "centro_caf", label: "Centro Operatori CAF", template: "caf" },
  { id: "centro_caaf", label: "Centro Operatori CAAF", template: "caf" },
  { id: "dpo", label: "Data Protection Officer (DPO)", template: "dpo" },
  { id: "organismo_mediazione", label: "Organismi di Mediazione", template: "mediazione" },
  { id: "mediatore", label: "Mediatore Libero Professionista", template: "mediazione" },
];

const STEPS = {
  scudo: ["Anagrafica", "Informazioni Finanziarie", "Attività Specifiche", "Estensioni di Copertura", "Sinistrosità", "Dichiarazioni"],
  caf: ["Anagrafica", "Informazioni Finanziarie", "Estensioni di Copertura", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
  dpo: ["Anagrafica", "Informazioni Finanziarie", "Informazioni Generali", "Estensioni di Copertura", "Sinistrosità", "Dichiarazioni"],
  mediazione: ["Anagrafica", "Informazioni Finanziarie", "Condizioni di Polizza", "Sinistrosità", "Assicurazioni Precedenti", "Dichiarazioni"],
};

const ESTENSIONI_SCUDO = [
  { key: "visto_pesante", label: "Visto Pesante (Cond. Part. Aggiuntiva B)" },
  { key: "insolvenze", label: "Insolvenze, liquidazioni e curatele (Cond. Part. Aggiuntiva C)" },
  { key: "visto_leggero", label: "Visto Leggero (Cond. Part. Aggiuntiva D)" },
  { key: "mediazione", label: "Attività di mediazione (Cond. Part. Aggiuntiva F)" },
  { key: "sovraindebitamento", label: "Gestione Crisi da Sovraindebitamento (Cond. Part. Aggiuntiva G)" },
  { key: "sicurezza_informatica", label: "Sicurezza informatica e perdita dei dati (Cond. Part. Aggiuntiva H)" },
  { key: "dpo_est", label: "Data Protection Officer (Cond. Part. Aggiuntiva I)" },
  { key: "resp_amministrativa", label: "Copertura responsabilità amministrativa (Cond. Part. Aggiuntiva J)" },
  { key: "fusioni_acquisizioni", label: "Attività di fusioni e acquisizioni (Cond. Part. Aggiuntiva K)" },
  { key: "resp_solido", label: "Responsabilità in solido (Cond. Part. Aggiuntiva L)" },
];

const ESTENSIONI_DPO = [
  { key: "copyright", label: "Violazione di copyright, brevetti o marchi (Cond. Part. A)" },
  { key: "diffamazione", label: "Ingiuria e diffamazione (Art 8.19)" },
  { key: "segreto_ufficio", label: "Violazione non intenzionale del segreto d'ufficio (Cond. Part. B)" },
  { key: "usa_canada", label: "Estensione copertura a Stati Uniti e Canada (Cond. Part. D)" },
];

const ESTENSIONI_CAF = [
  { key: "sicurezza_informatica", label: "Sicurezza informatica e perdita dei dati (Cond. Part. Aggiuntiva H)" },
  { key: "resp_solido", label: "Responsabilità in solido (Cond. Part. Aggiuntiva L)" },
  { key: "scoperto_10", label: "Scoperto del 10% (min \u20ac3.000 / max \u20ac10.000) per sinistri superiori a \u20ac20.000" },
];

export default function EconomicoGiuridicoPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step, selectedCategory]);

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const handleFileUpload = (docKey, fileData) => {
    setFiles(prev => { const n = { ...prev }; if (fileData) n[docKey] = fileData; else delete n[docKey]; return n; });
  };

  const showClaimsSheet = formData.sinistri_5_anni === "si" || formData.fatti_risarcimento === "si";
  useEffect(() => {
    if (showClaimsSheet && (!formData.sinistri_claims || formData.sinistri_claims.length === 0)) {
      setFormData(prev => ({ ...prev, sinistri_claims: [emptyClaim()] }));
    }
  }, [showClaimsSheet, formData.sinistri_claims]);

  const cat = CATEGORIES.find(c => c.id === selectedCategory);
  const template = cat?.template || "scudo";
  const steps = STEPS[template];
  const totalSteps = steps?.length || 6;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleSubmit = async () => {
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono) {
      toast.error("Compila tutti i campi obbligatori dell'anagrafica");
      return;
    }
    if (!formData.privacy) {
      toast.error("Devi accettare la privacy policy");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, {
        type: "economico_giuridico",
        data: { ...formData, categoria: selectedCategory, template },
        files: Object.values(files),
        contact: { nome: formData.nome, cognome: formData.cognome, email: formData.email, telefono: formData.telefono }
      });
      navigate("/conferma");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ===== CATEGORY SELECTION ===== */
  if (!selectedCategory) {
    return (
      <><Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="eco-giuridico-page">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-3" data-testid="eco-title">
              RC Professioni Economiche e Giuridiche
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
              Seleziona la tua professione per compilare il questionario dedicato alla tua attività.
            </p>
          </div>
          <div className="space-y-3">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                className="w-full text-left bg-white rounded-2xl border border-[#E5E2DB] p-5 premium-card hover:border-[#163321]/30 transition-all group"
                data-testid={`eco-cat-${c.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-base font-medium text-[#0F1F14] group-hover:text-[#163321] transition-colors">{c.label}</h3>
                  <ChevronRight className="w-5 h-5 text-[#4A5E51] group-hover:text-[#163321] group-hover:translate-x-1 transition-all shrink-0 ml-4" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </main><Footer /></>
    );
  }

  /* ===== STEP RENDERING ===== */
  const renderStep = () => {
    const stepName = steps[step];
    if (stepName === "Anagrafica") return renderAnagrafica();
    if (stepName === "Informazioni Finanziarie") return renderFinanziarie();
    if (stepName === "Attività Specifiche") return renderAttivitaSpecifiche();
    if (stepName === "Estensioni di Copertura") return renderEstensioni();
    if (stepName === "Informazioni Generali") return renderInfoGenerali();
    if (stepName === "Condizioni di Polizza") return renderCondizioni();
    if (stepName === "Sinistrosità") return renderSinistri();
    if (stepName === "Assicurazioni Precedenti") return renderAssicurazioniPrec();
    if (stepName === "Dichiarazioni") return renderDichiarazioni();
    return null;
  };

  const isStudio = selectedCategory?.startsWith("studio") || selectedCategory?.startsWith("centro") || selectedCategory === "organismo_mediazione";

  /* --- ANAGRAFICA --- */
  const renderAnagrafica = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Inserisci i dati anagrafici del proponente/contraente.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Nome" required value={formData.nome} onChange={v => update("nome", v)} tid="eco-nome" />
        <FI label="Cognome" required value={formData.cognome} onChange={v => update("cognome", v)} tid="eco-cognome" />
      </div>
      <GeoLocationInput label="Sede Legale o Residenza" required value={formData.indirizzo} onChange={v => update("indirizzo", v)} testId="eco-indirizzo" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FI label="Città" required value={formData.citta} onChange={v => update("citta", v)} tid="eco-citta" />
        <FI label="Provincia" value={formData.provincia} onChange={v => update("provincia", v)} tid="eco-provincia" />
        <FI label="CAP" value={formData.cap} onChange={v => update("cap", v)} tid="eco-cap" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Codice Fiscale" required value={formData.codice_fiscale} onChange={v => update("codice_fiscale", v.toUpperCase())} tid="eco-cf" />
        <FI label="Partita IVA" required value={formData.partita_iva} onChange={v => update("partita_iva", v)} tid="eco-piva" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FI label="Email" required type="email" value={formData.email} onChange={v => update("email", v)} tid="eco-email" placeholder="la.tua@email.it" />
        <FI label="Telefono cellulare" required type="tel" value={formData.telefono} onChange={v => update("telefono", v)} tid="eco-tel" placeholder="+39 333 1234567" />
      </div>
      <FI label="Anno di iscrizione all'albo" value={formData.anno_albo} onChange={v => update("anno_albo", v)} tid="eco-anno-albo" />
      {isStudio && (
        <div className="space-y-4 p-4 bg-[#F5F4F0] rounded-xl">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4A5E51]">Dati Studio / Società</p>
          <FI label="Ragione / Denominazione Sociale" required value={formData.ragione_sociale} onChange={v => update("ragione_sociale", v)} tid="eco-rag-soc" />
          <FI label="Anno di costituzione" value={formData.anno_costituzione} onChange={v => update("anno_costituzione", v)} tid="eco-anno-cost" />
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Professionisti iscritti all'albo / collaboratori in garanzia</Label>
            <Textarea value={formData.professionisti_studio || ""} onChange={e => update("professionisti_studio", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Nome e Cognome - Anno iscrizione albo (uno per riga)" data-testid="eco-prof-studio" />
          </div>
        </div>
      )}
    </div>
  );

  /* --- INFORMAZIONI FINANZIARIE --- */
  const renderFinanziarie = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Informazioni sul fatturato dell'attività.</p>
      <FI label={enrichLabelWithYear("Fatturato ultimo esercizio finanziario completo (\u20ac)")} required value={formData.fatturato_ultimo} onChange={v => update("fatturato_ultimo", v)} type="number" tid="eco-fatt-ultimo" />
      <FI label={enrichLabelWithYear("Fatturato penultimo esercizio finanziario completo (\u20ac)")} required value={formData.fatturato_penultimo} onChange={v => update("fatturato_penultimo", v)} type="number" tid="eco-fatt-pen" />
      <FI label="Stima fatturato esercizio corrente (&euro;)" required value={formData.fatturato_stima} onChange={v => update("fatturato_stima", v)} type="number" tid="eco-fatt-stima" />
      <YN label="Professionista neo abilitato o Studio/Società neo costituita?" field="neo_abilitato" formData={formData} update={update} tid="eco-neo" />
      {formData.neo_abilitato === "si" && (
        <FI label="Fatturato previsto per i primi 12 mesi di attività (&euro;)" required value={formData.fatturato_previsto} onChange={v => update("fatturato_previsto", v)} type="number" tid="eco-fatt-prev" />
      )}
      {template === "dpo" && (
        <div className="space-y-2">
          <Label className="text-sm text-[#0F1F14]">3 maggiori compensi ricevuti nell'ultima annualità</Label>
          <Textarea value={formData.maggiori_compensi || ""} onChange={e => update("maggiori_compensi", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="1. Importo - Committente&#10;2. Importo - Committente&#10;3. Importo - Committente" data-testid="eco-compensi" />
        </div>
      )}
    </div>
  );

  /* --- ATTIVITA SPECIFICHE (solo Scudo) --- */
  const renderAttivitaSpecifiche = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Domande specifiche sulla tua attività professionale.</p>
      <YN label="Richiede l'inclusione in copertura di attività sindacale e/o di revisione interna e/o esterna e/o di membro dell'Organismo di Vigilanza?" field="att_sindacale" formData={formData} update={update} tid="eco-sindacale" />
      {formData.att_sindacale === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <YN label="Conferma di NON essere stato revisore/sindaco/OdV di società in procedure concorsuali o quotate?" field="no_concorsuali" formData={formData} update={update} tid="eco-no-conc" />
          <YN label="Negli ultimi 24 mesi nessuna società con incarico ha registrato perdita d'esercizio o patrimonio netto negativo?" field="no_perdite" formData={formData} update={update} tid="eco-no-perdite" />
          <FI label="Percentuale fatturato riconducibile a tale attività (%)" value={formData.perc_fatturato_sindacale} onChange={v => update("perc_fatturato_sindacale", v)} type="number" tid="eco-perc-sind" />
          <div className="space-y-2">
            <Label className="text-sm text-[#0F1F14]">Società per le quali è svolta l'attività</Label>
            <Textarea value={formData.societa_sindacale || ""} onChange={e => update("societa_sindacale", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Ragione Sociale - Data nomina - In corso/Cessato (uno per riga)" data-testid="eco-soc-sind" />
          </div>
          <YN label="Desidera introdurre lo Scoperto del 10% (min &euro;5.000 / max &euro;15.000) per sinistri alto rischio superiori a &euro;20.000?" field="scoperto_sindacale" formData={formData} update={update} tid="eco-scop-sind" />
          <YN label="Accetta il sottolimite pari al 50% del massimale di polizza per attività sindacale?" field="sottolimite_sindacale" formData={formData} update={update} tid="eco-sottolim" />
        </div>
      )}
      <YN label="Richiede l'inclusione in copertura di attività svolta come membro del Consiglio d'Amministrazione?" field="att_cda" formData={formData} update={update} tid="eco-cda" />
      <YN label="Richiede l'inclusione in copertura di attività EDP?" field="att_edp" formData={formData} update={update} tid="eco-edp" />
      {formData.att_edp === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FI label="Ragione/Denominazione Sociale attività EDP" value={formData.edp_ragione} onChange={v => update("edp_ragione", v)} tid="eco-edp-rag" />
          <FI label="Sede legale attività EDP" value={formData.edp_sede} onChange={v => update("edp_sede", v)} tid="eco-edp-sede" />
          <FI label="Fatturato attività EDP (&euro;)" value={formData.edp_fatturato} onChange={v => update("edp_fatturato", v)} type="number" tid="eco-edp-fatt" />
        </div>
      )}
    </div>
  );

  /* --- ESTENSIONI DI COPERTURA --- */
  const renderEstensioni = () => {
    const ESTENSIONI_MAP = { scudo: ESTENSIONI_SCUDO, dpo: ESTENSIONI_DPO, caf: ESTENSIONI_CAF };
    const list = ESTENSIONI_MAP[template] || ESTENSIONI_CAF;
    return (
      <div className="space-y-5">
        <p className="text-sm text-[#4A5E51]">Seleziona le estensioni di copertura desiderate.</p>
        {list.map(ext => (
          <YN key={ext.key} label={ext.label} field={`est_${ext.key}`} formData={formData} update={update} tid={`eco-est-${ext.key}`} />
        ))}
        {(template === "scudo" || template === "caf") && (
          <>
            <YN label="Richiede l'estensione di copertura &quot;Tutela Legale&quot;?" field="tutela_legale" formData={formData} update={update} tid="eco-tutela" />
            {template === "scudo" && <YN label="Richiede la copertura addizionale &quot;Infortuni&quot;?" field="infortuni" formData={formData} update={update} tid="eco-infortuni" />}
          </>
        )}
        {template === "dpo" && (
          <div className="space-y-4">
            <FI label="Massimale richiesto (per sinistro e in aggregato annuo)" value={formData.massimale_richiesto} onChange={v => update("massimale_richiesto", v)} tid="eco-massimale" />
            <FI label="Franchigia richiesta" value={formData.franchigia_richiesta} onChange={v => update("franchigia_richiesta", v)} tid="eco-franchigia" />
            <FI label="Anni di retroattività richiesti" value={formData.retroattivita_anni} onChange={v => update("retroattivita_anni", v)} tid="eco-retro" />
          </div>
        )}
      </div>
    );
  };

  /* --- INFORMAZIONI GENERALI (solo DPO) --- */
  const renderInfoGenerali = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Informazioni specifiche sulla tua attività di DPO.</p>
      <FI label="Anno di inizio attività" value={formData.anno_inizio} onChange={v => update("anno_inizio", v)} tid="eco-anno-inizio" />
      <YN label="Prevedete cambiamenti sostanziali nella tipologia di attività nei prossimi 12 mesi?" field="cambiamenti_12m" formData={formData} update={update} tid="eco-cambiamenti" />
      {formData.cambiamenti_12m === "si" && (
        <Textarea value={formData.dettagli_cambiamenti || ""} onChange={e => update("dettagli_cambiamenti", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Descrivi i cambiamenti previsti..." data-testid="eco-det-cambiamenti" />
      )}
      <YN label="Ricorrete a personale/entità esterne per attività di cui siete responsabili (Outsourcing)?" field="outsourcing" formData={formData} update={update} tid="eco-outsourcing" />
      {formData.outsourcing === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <Textarea value={formData.outsourcing_desc || ""} onChange={e => update("outsourcing_desc", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[60px]" placeholder="Descrizione attività esternalizzate..." data-testid="eco-out-desc" />
          <YN label="Verificata l'esistenza e adeguatezza di polizza assicurativa dei soggetti esterni?" field="outsourcing_polizza" formData={formData} update={update} tid="eco-out-polizza" />
          <FI label="Massimale delle polizze dei soggetti esterni" value={formData.outsourcing_massimale} onChange={v => update("outsourcing_massimale", v)} tid="eco-out-mass" />
          <FI label="Compensi spettanti ai soggetti esterni (&euro;)" value={formData.outsourcing_compensi} onChange={v => update("outsourcing_compensi", v)} type="number" tid="eco-out-comp" />
        </div>
      )}
      <YN label="Svolgete attività di DPO presso società con fatturato superiore a &euro;25.000.000?" field="dpo_25m" formData={formData} update={update} tid="eco-dpo-25m" />
    </div>
  );

  /* --- CONDIZIONI POLIZZA (solo Mediazione) --- */
  const renderCondizioni = () => (
    <div className="space-y-5">
      <p className="text-sm text-[#4A5E51]">Indica le condizioni di polizza desiderate.</p>
      <FI label="Massimale richiesto (per sinistro e in aggregato annuo)" value={formData.massimale_richiesto} onChange={v => update("massimale_richiesto", v)} tid="eco-massimale" />
      <FI label="Franchigia richiesta" value={formData.franchigia_richiesta} onChange={v => update("franchigia_richiesta", v)} tid="eco-franchigia" />
      <FI label="Anni di retroattività richiesti (decorrenza)" value={formData.retroattivita_anni} onChange={v => update("retroattivita_anni", v)} tid="eco-retro" />
    </div>
  );

  /* --- SINISTROSITA --- */
  const renderSinistri = () => (
    <div className="space-y-5">
      <YN label="Sono state avanzate richieste di risarcimento o azioni per accertare responsabilità civile, penale o deontologica negli ultimi 5 anni?" field="sinistri_5_anni" formData={formData} update={update} tid="eco-sinistri-5" />
      <YN label="È a conoscenza di azioni, omissioni o fatti da cui possano derivare richieste di risarcimento?" field="fatti_risarcimento" formData={formData} update={update} tid="eco-fatti" />

      {showClaimsSheet && formData.sinistri_claims && formData.sinistri_claims.length > 0 && (
        <div className="mt-4 p-4 sm:p-5 bg-amber-50/50 border border-amber-200/60 rounded-2xl space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            In caso di risposta affermativa, compilare il <strong>Modulo CIS</strong> sottostante per ciascun sinistro o circostanza.
          </div>
          <ClaimsInfoSheet
            claims={formData.sinistri_claims}
            onClaimsChange={(updated) => update("sinistri_claims", updated)}
            config={CIS_ECO_CONFIG}
          />
        </div>
      )}

      <FileUploadZone label="Upload documenti aggiuntivi" hint="Carica eventuali documenti rilevanti a supporto (perizie, lettere, atti)"
        required={false}
        onFileUploaded={data => handleFileUpload("cis", data)} />
      <div className="space-y-2">
        <Label className="text-sm text-[#0F1F14]">Note aggiuntive</Label>
        <Textarea value={formData.dettagli_sinistri || ""} onChange={e => update("dettagli_sinistri", e.target.value)} className="rounded-xl border-[#E5E2DB] min-h-[80px]" placeholder="Eventuali ulteriori dettagli o circostanze rilevanti..." data-testid="eco-det-sinistri" />
      </div>
    </div>
  );

  /* --- ASSICURAZIONI PRECEDENTI --- */
  const renderAssicurazioniPrec = () => (
    <div className="space-y-5">
      <YN label="Il Proponente è mai stato assicurato per la RC Professionale?" field="gia_assicurato" formData={formData} update={update} tid="eco-gia-assic" />
      {formData.gia_assicurato === "si" && (
        <div className="space-y-4 pl-4 border-l-2 border-[#163321]/10">
          <FI label="Assicuratori precedenti" required value={formData.assicuratori_prec} onChange={v => update("assicuratori_prec", v)} tid="eco-assic-prec" />
          <FI label="Data scadenza ultima copertura" type="date" value={formData.data_scadenza} onChange={v => update("data_scadenza", v)} tid="eco-data-scad" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FI label="Franchigia" value={formData.franchigia_prec} onChange={v => update("franchigia_prec", v)} tid="eco-franch-prec" />
            <FI label="Massimale" value={formData.massimale_prec} onChange={v => update("massimale_prec", v)} tid="eco-mass-prec" />
          </div>
          <FI label="Numero anni copertura continua" type="number" value={formData.anni_copertura} onChange={v => update("anni_copertura", v)} tid="eco-anni-cop" />
          <FI label="Ultimo Premio Pagato (&euro;)" required type="number" value={formData.ultimo_premio} onChange={v => update("ultimo_premio", v)} tid="eco-ultimo-premio" />
        </div>
      )}
    </div>
  );

  /* --- DICHIARAZIONI --- */
  const renderDichiarazioni = () => (
    <div className="space-y-6">
      <p className="text-sm text-[#4A5E51]">Conferma di aver preso visione dei documenti e accetta le dichiarazioni.</p>
      <DichiarazioniBlock formData={formData} update={update} />
      <div className="flex items-start gap-3 p-4 bg-[#F5F4F0] rounded-xl">
        <Checkbox id="set_info_eco" checked={formData.set_informativo || false} onCheckedChange={v => update("set_informativo", v)} className="mt-0.5 data-[state=checked]:bg-[#163321]" data-testid="eco-set-info" />
        <label htmlFor="set_info_eco" className="text-xs text-[#4A5E51] leading-relaxed cursor-pointer">
          <strong>Set Informativo</strong> <span className="text-[#D97B58]">*</span><br />
          Dichiaro di aver ricevuto e preso visione dei documenti del SET INFORMATIVO (Regolamento IVASS n&deg; 41/2018).
        </label>
      </div>
    </div>
  );

  return (
    <><Header />
    <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="eco-form-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <button onClick={() => { setSelectedCategory(null); setStep(0); setFormData({}); setFiles({}); }} className="text-xs text-[#D97B58] hover:underline mb-2 inline-block" data-testid="eco-back-categories">&larr; Cambia professione</button>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="eco-form-title">RC Professioni Economiche e Giuridiche</h1>
          <p className="text-sm text-[#4A5E51]">{cat?.label}</p>
        </div>
        <StepProgress steps={steps} currentStep={step} onStepClick={setStep} progress={progress} testIdPrefix="eco" />
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 mb-6 shadow-sm" data-testid={`eco-step-${step}`}>
          <h2 className="font-heading text-lg font-medium text-[#0F1F14] mb-5">{steps[step]}</h2>
          {renderStep()}
        </div>
        <FormNavigation
          step={step} totalSteps={totalSteps}
          onPrev={() => step === 0 ? setSelectedCategory(null) : setStep(s => s - 1)}
          onNext={() => setStep(s => s + 1)}
          onSubmit={handleSubmit} submitting={submitting} canSubmit={!!formData.privacy}
          prevLabel={step === 0 ? "Professioni" : "Indietro"} testIdPrefix="eco"
        />
      </div>
      <PrivacyLinks className="mt-6 mb-4" />
    </main><Footer /></>
  );
}
